import React, { ComponentProps, FC, useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import {
  ENTITLEMENTS_PLATFORM_TYPES,
  useAlloyFeatureFlag,
} from '@adsk/alloy-provider';
import { BasicButton } from '@adsk/alloy-react-button';
import {
  LogoAutodeskBim360Icon,
  LogoAutodeskAccIcon,
  TriangleUpIcon,
  TriangleDownIcon,
} from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';

import ProjectLoader from './ProjectLoader';
import ProjectPicker from './ProjectPicker';
import ProjectPickerWithTabs from './ProjectPickerWithTabs';
import {
  accountsListShape,
  projectsListShape,
  projectShape,
} from '../helpers/propTypes';
import { Account, Project } from '../helpers/types';

type ProjectButtonProps = {
  accounts?: Account[];
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projects?: Project[];
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_templates?: Project[];
  projectId?: string;
  accountId?: string;
  productId?: string;
  currentProject?: Project;
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projectsLoading?: boolean;
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projectsError?: boolean;
  /** @deprecated ignored when FF (entitlements-backend-search) enabled and will be removed in future versions */
  UNSAFE_projectsRetry?: () => void;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  renderProjectPicker?: (
    props: ComponentProps<typeof ProjectPicker>,
  ) => JSX.Element;
};

const ProjectPickerWithFF = (props: ComponentProps<typeof ProjectPicker>) => {
  const { value: backendSearchFF } = useAlloyFeatureFlag(
    'entitlements-backend-search',
  );
  const ProjectPickerComponent = backendSearchFF
    ? ProjectPickerWithTabs
    : ProjectPicker;

  return <ProjectPickerComponent {...props} />;
};

/**
 * Acs ProjectButton
 */
const ProjectButton: FC<ProjectButtonProps> = ({
  accounts,
  UNSAFE_projects: projects,
  UNSAFE_templates: templates,
  accountId,
  projectId,
  productId,
  currentProject: currentProjectProp,
  UNSAFE_projectsLoading: projectsLoading,
  UNSAFE_projectsError: projectsError,
  UNSAFE_projectsRetry: projectsRetry,
  onAccountClick,
  onProjectClick,
  renderProjectPicker = (props: ComponentProps<typeof ProjectPicker>) => (
    <ProjectPickerWithFF {...props} />
  ),
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);
  const openMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);
  const toggleMenu = useCallback(() => {
    setMenuOpen((v) => !v);
  }, []);
  const currentAccount =
    (accounts &&
      accountId &&
      accounts.find((p) => p.account_id === accountId)) ||
    undefined;
  const currentProjectFromProp =
    currentProjectProp?.classification !== 'template'
      ? currentProjectProp
      : undefined;
  const currentProject =
    currentProjectFromProp ||
    (projects && projectId && projects.find((p) => p.id === projectId)) ||
    undefined;
  const currentTemplateFromProp =
    currentProjectProp?.classification === 'template'
      ? currentProjectProp
      : undefined;
  const currentTemplate =
    currentTemplateFromProp ||
    (projectId &&
      templates &&
      templates.find((template) => template.id === projectId));

  const LogoComponent = currentProject
    ? currentProject.platform === ENTITLEMENTS_PLATFORM_TYPES.ACC
      ? LogoAutodeskAccIcon
      : LogoAutodeskBim360Icon
    : undefined;

  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;

  return currentProject || currentAccount || currentTemplate ? (
    <Flyout
      show={menuOpen}
      onHide={closeMenu}
      onShow={openMenu}
      style={{ maxWidth: 'fit-content' }}
      offset={[0, 15]}
      containerPadding={0}
      placement={Flyout.PLACEMENTS.BOTTOM}
      content={renderProjectPicker({
        currentAccountId: accountId,
        accounts,
        projects,
        templates,
        currentProjectId: projectId,
        currentProductId: productId,
        projectsLoading,
        projectsError,
        projectsRetry,
        onClose: closeMenu,
        onAccountClick,
        onProjectClick,
      })}
    >
      <BasicButton
        onClick={toggleMenu}
        data-testid="TopNavigation__projects"
        style={{
          padding: 0,
          justifyContent: 'start',
          color: theme.colors.charcoal900,
          '&:hover': {
            color: theme.colors.charcoal700,
          },
        }}
      >
        {!currentTemplate && (
          <span
            css={[
              {
                display: 'inline-block',
                overflow: 'hidden',
                flexShrink: 0,
                borderRadius: 4,
                width: 32,
                height: 32,
              },
            ]}
          >
            <img
              src={currentProject?.imageUrl || currentAccount?.image_url}
              data-testid="project-image"
              width={32}
              height={32}
            />
          </span>
        )}

        {/*  Template Only */}
        {currentTemplate && (
          <>
            <span
              css={[
                theme.typography.bodyMedium,
                {
                  color: theme.colors.charcoal500,
                  marginRight: '12px',
                },
              ]}
            >
              {T.translate('PROJECT_TEMPLATE')}
            </span>
            <span
              css={[
                theme.typography.bodyMediumBold,
                theme.typography.ellipsis,
                {
                  color: theme.colors.charcoal900,
                  marginRight: '8px',
                  maxWidth: 300,
                  [theme.mediaQuery.medium]: {
                    maxWidth: 100,
                  },
                },
              ]}
            >
              {currentTemplate?.name}
            </span>
          </>
        )}

        {currentProject && (
          <span
            css={[
              {
                textAlign: 'left',
                marginLeft: 15,
                maxWidth: 300,
                [theme.mediaQuery.medium]: {
                  maxWidth: 100,
                },
                [theme.mediaQuery.small]: {
                  display: 'none',
                },
              },
            ]}
          >
            {/*  Project And Account */}
            {currentAccount && (
              <div
                css={[
                  theme.typography.caption,
                  theme.typography.ellipsis,
                  {
                    color: theme.colors.charcoal700,
                    textAlign: 'left',
                  },
                ]}
              >
                {currentAccount.account_display_name}
              </div>
            )}

            {/*  Project Only */}
            <div
              css={[theme.typography.bodyMediumBold, theme.typography.ellipsis]}
            >
              <span>{currentProject?.name}</span>
            </div>
          </span>
        )}

        {/*  Account Only */}
        {!currentProject && currentAccount && (
          <span
            css={[
              {
                textAlign: 'left',
                marginLeft: 15,
                maxWidth: 300,
                [theme.mediaQuery.medium]: {
                  maxWidth: 100,
                },
                [theme.mediaQuery.small]: {
                  display: 'none',
                },
              },
            ]}
          >
            <div
              css={[theme.typography.bodyMediumBold, theme.typography.ellipsis]}
            >
              <span>{currentAccount.account_display_name}</span>
            </div>
          </span>
        )}

        {LogoComponent && (
          <LogoComponent
            size={16}
            style={{
              margin: `0px 4px -${!currentAccount ? 2 : 13}px 9px`,
              color: theme.colors.charcoal700,
              [theme.mediaQuery.small]: {
                display: 'none',
              },
            }}
          />
        )}

        <TriangleIconComponent
          style={{
            color: theme.colors.charcoal700,
            marginBottom: currentProject && currentAccount ? '-12px' : 0,

            [theme.mediaQuery.small]: {
              margin: '0 4px',
            },
          }}
          size={20}
        />
      </BasicButton>
    </Flyout>
  ) : (
    <ProjectLoader />
  );
};

ProjectButton.displayName = 'ProjectButton';

ProjectButton.propTypes = {
  accounts: accountsListShape,
  UNSAFE_projects: projectsListShape,
  UNSAFE_templates: projectsListShape,
  accountId: PropTypes.string,
  projectId: PropTypes.string,
  productId: PropTypes.string,
  currentProject: projectShape,
  UNSAFE_projectsLoading: PropTypes.bool,
  UNSAFE_projectsError: PropTypes.bool,
  UNSAFE_projectsRetry: PropTypes.func,
  onProjectClick: PropTypes.func,
  onAccountClick: PropTypes.func,
  renderProjectPicker: PropTypes.func,
};

export default ProjectButton;
